import React from "react";
import { Link } from "react-router-dom";
import { Shield, Cookie, Mail, ArrowLeft, Lock, Database, Eye, Globe2, FileCheck, Trash2 } from "lucide-react";
import { AdSenseSlot } from "../components/AdSenseSlot";

// Date de dernière mise à jour — à modifier manuellement quand on édite cette page
const LAST_UPDATE = "27 avril 2026";

const Section = ({ icon: Icon, title, id, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-red/20 to-brand-orange/20 text-brand-orange flex items-center justify-center">
        <Icon size={18} />
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-black">{title}</h2>
    </div>
    <div className="text-white/75 leading-relaxed space-y-3 text-[15px] pl-1">
      {children}
    </div>
  </section>
);

const TOC = () => (
  <nav className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sticky top-6" aria-label="Sommaire">
    <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">Sommaire</p>
    <ul className="space-y-2 text-sm">
      {[
        ["editeur", "1. Éditeur du site"],
        ["donnees", "2. Données collectées"],
        ["finalites", "3. Finalités"],
        ["base-legale", "4. Base légale"],
        ["partage", "5. Partage des données"],
        ["cookies", "6. Cookies & traceurs"],
        ["adsense", "7. Google AdSense"],
        ["duree", "8. Durée de conservation"],
        ["droits", "9. Tes droits (RGPD)"],
        ["securite", "10. Sécurité"],
        ["mineurs", "11. Mineurs"],
        ["modifications", "12. Modifications"],
        ["contact", "13. Contact"],
      ].map(([id, label]) => (
        <li key={id}>
          <a href={`#${id}`} className="text-white/60 hover:text-brand-orange transition-colors">{label}</a>
        </li>
      ))}
    </ul>
  </nav>
);

export const PrivacyPage = () => {
  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="privacy-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-6">
        <ArrowLeft size={14} /> Retour à l'accueil
      </Link>

      <header className="mb-10">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-bold uppercase tracking-widest mb-4">
          <Shield size={11} /> Confidentialité
        </span>
        <h1 className="font-display text-4xl md:text-6xl font-black mb-3 tracking-tight">
          Politique de confidentialité
        </h1>
        <p className="text-white/60 max-w-3xl">
          Voamada respecte ta vie privée. Cette page explique <strong>quelles données</strong> nous collectons,
          <strong> pourquoi</strong>, et <strong>comment exercer tes droits</strong>. Document conforme au
          Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la Loi malgache n° 2014-038
          relative à la protection des données à caractère personnel.
        </p>
        <p className="text-xs text-white/40 mt-3">Dernière mise à jour : {LAST_UPDATE}</p>
      </header>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block">
          <TOC />
        </aside>

        <div className="space-y-12 max-w-3xl">

          <Section id="editeur" icon={FileCheck} title="1. Éditeur du site">
            <p>Le site <strong>voamada.com</strong> (ci-après "Voamada") est édité par le projet <strong>Voamada</strong>, plateforme
            indépendante dédiée à la promotion des talents malgaches et africains.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Hébergement : OVHcloud SAS, 2 rue Kellermann, 59100 Roubaix, France</li>
              <li>Contact général : <a className="text-brand-orange hover:underline" href="mailto:contact@voamada.com">contact@voamada.com</a></li>
              <li>Délégué à la protection des données (DPO) : <a className="text-brand-orange hover:underline" href="mailto:privacy@voamada.com">privacy@voamada.com</a></li>
            </ul>
          </Section>

          <Section id="donnees" icon={Database} title="2. Quelles données collectons-nous ?">
            <p><strong>a) Données fournies par toi (compte utilisateur)</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Nom / pseudo, adresse email, mot de passe (chiffré via bcrypt — jamais lisible en clair)</li>
              <li>Optionnel : photo de profil, biographie, pays, ville, catégorie artistique, liens sociaux</li>
              <li>Si tu es artiste : tracks/vidéos publiés, descriptions, miniatures</li>
              <li>Messages échangés via la messagerie ou le chat général</li>
            </ul>
            <p className="mt-3"><strong>b) Données collectées automatiquement</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Adresse IP (anonymisée après 13 mois)</li>
              <li>Type d'appareil, navigateur, système d'exploitation, résolution écran</li>
              <li>Pages visitées, durée de visite, source de provenance</li>
              <li>Tokens d'authentification (JWT) stockés localement dans ton navigateur</li>
            </ul>
            <p className="mt-3"><strong>c) Données issues de tiers</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Si tu te connectes via Google : ton nom, email et photo de profil Google</li>
              <li>Métadonnées des contenus que tu importes depuis YouTube/TikTok/Instagram (titre, miniature)</li>
            </ul>
          </Section>

          <Section id="finalites" icon={Eye} title="3. Pourquoi (finalités) ?">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Fournir le service :</strong> création de compte, hébergement de tes contenus, recommandations personnalisées</li>
              <li><strong>Communication :</strong> notifications de nouveaux abonnés, messages reçus, demandes de candidatures</li>
              <li><strong>Sécurité :</strong> détection de fraude, lutte contre le spam et les abus</li>
              <li><strong>Statistiques anonymes :</strong> améliorer la plateforme et identifier les contenus populaires</li>
              <li><strong>Publicité :</strong> diffusion d'annonces (sponsors directs et Google AdSense — voir section 7)</li>
              <li><strong>Programme Vola :</strong> récompenses, parrainage, cash-out</li>
              <li><strong>Obligations légales :</strong> conservation requise par la loi (facturation, modération)</li>
            </ul>
          </Section>

          <Section id="base-legale" icon={FileCheck} title="4. Sur quelle base légale ?">
            <p>Conformément au RGPD, chaque traitement de tes données repose sur une base légale précise :</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li><strong>Exécution du contrat</strong> (CGU acceptées) — création de compte, publication de contenus, messagerie</li>
              <li><strong>Intérêt légitime</strong> — sécurité, statistiques anonymisées, lutte anti-fraude</li>
              <li><strong>Ton consentement</strong> — cookies non essentiels, publicité personnalisée, newsletter</li>
              <li><strong>Obligation légale</strong> — conservation comptable, signalements à l'autorité judiciaire si requis</li>
            </ul>
          </Section>

          <Section id="partage" icon={Globe2} title="5. Avec qui partageons-nous tes données ?">
            <p>Voamada <strong>ne vend jamais</strong> tes données. Elles sont partagées uniquement avec :</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li><strong>OVHcloud</strong> (hébergeur — France) : stockage des bases de données et fichiers</li>
              <li><strong>Google AdSense</strong> (publicité — USA, Privacy Shield + SCC) : voir section 7</li>
              <li><strong>YouTube / TikTok / Instagram / Facebook</strong> : si tu y consultes du contenu intégré, leurs propres règles s'appliquent</li>
              <li><strong>Autorités</strong> : uniquement sur réquisition judiciaire valide</li>
            </ul>
            <p className="mt-3 text-sm text-white/60">Les autres utilisateurs voient uniquement les données que tu as choisi de rendre publiques (profil, contenus publiés).</p>
          </Section>

          <Section id="cookies" icon={Cookie} title="6. Cookies & traceurs">
            <p>Voamada utilise plusieurs catégories de cookies. Tu peux les gérer à tout moment via la bannière de consentement
            ou les paramètres de ton navigateur.</p>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
                <thead className="bg-white/[0.04]">
                  <tr>
                    <th className="text-left p-3 font-bold">Type</th>
                    <th className="text-left p-3 font-bold">Finalité</th>
                    <th className="text-left p-3 font-bold">Durée</th>
                    <th className="text-left p-3 font-bold">Consentement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr><td className="p-3">Essentiels (auth)</td><td className="p-3">Te garder connecté</td><td className="p-3">30 jours</td><td className="p-3 text-emerald-400">Obligatoire</td></tr>
                  <tr><td className="p-3">Préférences (langue, thème)</td><td className="p-3">Mémoriser tes choix</td><td className="p-3">1 an</td><td className="p-3 text-emerald-400">Obligatoire</td></tr>
                  <tr><td className="p-3">Statistiques anonymes</td><td className="p-3">Comprendre l'usage</td><td className="p-3">13 mois</td><td className="p-3 text-brand-yellow">Optionnel</td></tr>
                  <tr><td className="p-3">Google AdSense</td><td className="p-3">Annonces personnalisées</td><td className="p-3">13 mois</td><td className="p-3 text-brand-yellow">Optionnel</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3"><Link to="/cookies" className="text-brand-orange hover:underline">→ Voir la liste détaillée et gérer mes préférences</Link></p>
          </Section>

          <Section id="adsense" icon={Globe2} title="7. Google AdSense — Publicité">            <p>Voamada affiche des publicités via le service <strong>Google AdSense</strong> pour financer la plateforme et
            permettre l'accès gratuit aux contenus.</p>
            <p>Nous utilisons le <strong>Google Consent Mode v2</strong> : tant que tu n'as pas accepté la publicité dans la
            bannière de consentement, <strong>aucun cookie publicitaire</strong> n'est posé et Google n'enregistre aucun
            identifiant te concernant (mode "ads_data_redaction"). Si tu refuses, des annonces non personnalisées
            (contextuelles) peuvent toujours s'afficher, mais sans suivi.</p>
            <p>Si tu acceptes, Google peut utiliser des cookies pour diffuser des annonces basées sur tes visites précédentes
            sur ce site ou d'autres sites. Tu peux <strong>retirer ton consentement à tout moment</strong> via :</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Le bouton <strong>"🍪 Gérer mes cookies"</strong> en bas de chaque page</li>
              <li><a className="text-brand-orange hover:underline" href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Paramètres Google Ads</a> (désactiver la publicité personnalisée)</li>
              <li><a className="text-brand-orange hover:underline" href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Aboutads.info</a> (États-Unis)</li>
              <li><a className="text-brand-orange hover:underline" href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">Youronlinechoices.com</a> (Europe)</li>
            </ul>
            <p className="mt-3">Pour plus d'infos : <a className="text-brand-orange hover:underline" href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Politique publicité Google</a>.</p>
          </Section>

          <Section id="duree" icon={Database} title="8. Combien de temps gardons-nous tes données ?">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Compte actif :</strong> tant que tu utilises Voamada</li>
              <li><strong>Compte inactif :</strong> supprimé automatiquement après <strong>3 ans</strong> sans connexion</li>
              <li><strong>Logs de connexion :</strong> 12 mois</li>
              <li><strong>Données comptables (factures Premium) :</strong> 10 ans (obligation légale)</li>
              <li><strong>Cookies :</strong> 13 mois maximum</li>
            </ul>
          </Section>

          {/* GOOGLE ADSENSE — In-article (long format SEO) */}
          <AdSenseSlot slotKey="articleInline" format="fluid" layout="in-article" minHeight={250} testid="ad-privacy-inline" />

          <Section id="droits" icon={Lock} title="9. Tes droits — RGPD">
            <p>Tu disposes des droits suivants, exerçables à tout moment :</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li><strong>Droit d'accès</strong> — obtenir une copie de toutes tes données</li>
              <li><strong>Droit de rectification</strong> — corriger les infos inexactes</li>
              <li><strong>Droit à l'effacement</strong> ("droit à l'oubli") — supprimer ton compte et tes données</li>
              <li><strong>Droit d'opposition</strong> — refuser certains traitements (publicité ciblée, statistiques)</li>
              <li><strong>Droit à la portabilité</strong> — récupérer tes données dans un format réutilisable (JSON)</li>
              <li><strong>Droit de limitation</strong> — geler temporairement le traitement</li>
              <li><strong>Droit de réclamation</strong> auprès de la CNIL (France) ou de la Commission Malagasy de l'Informatique (Madagascar)</li>
            </ul>
            <p className="mt-3">Pour exercer un droit : <a className="text-brand-orange hover:underline" href="mailto:privacy@voamada.com">privacy@voamada.com</a> — réponse sous 30 jours maximum.</p>
          </Section>

          <Section id="securite" icon={Shield} title="10. Sécurité">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Connexion HTTPS obligatoire (TLS 1.3)</li>
              <li>Mots de passe stockés en bcrypt (jamais en clair)</li>
              <li>Authentification par tokens JWT signés</li>
              <li>Sauvegardes quotidiennes des bases de données</li>
              <li>Hébergement OVH certifié ISO 27001</li>
              <li>Surveillance contre les attaques par force brute</li>
            </ul>
            <p className="mt-2 text-sm text-white/60">En cas de violation de données affectant tes informations, nous te notifions sous 72h conformément au RGPD.</p>
          </Section>

          <Section id="mineurs" icon={Trash2} title="11. Utilisateurs mineurs">
            <p>Voamada est ouvert dès <strong>13 ans</strong>. Pour les utilisateurs de 13 à 16 ans, l'accord parental
            est requis (RGPD art. 8). Si tu découvres qu'un mineur de moins de 13 ans utilise le service, contacte-nous
            à <a className="text-brand-orange hover:underline" href="mailto:privacy@voamada.com">privacy@voamada.com</a> pour suppression immédiate.</p>
          </Section>

          <Section id="modifications" icon={FileCheck} title="12. Modifications de cette politique">
            <p>Cette politique peut évoluer. La date de dernière mise à jour est affichée en haut de page.
            Pour toute modification substantielle, nous t'en informerons par email ou par bannière sur le site.</p>
          </Section>

          <Section id="contact" icon={Mail} title="13. Nous contacter">
            <p>Pour toute question concernant tes données :</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Email DPO : <a className="text-brand-orange hover:underline" href="mailto:privacy@voamada.com">privacy@voamada.com</a></li>
              <li>Email général : <a className="text-brand-orange hover:underline" href="mailto:contact@voamada.com">contact@voamada.com</a></li>
            </ul>
            <p className="mt-3 text-sm text-white/60">Voir aussi : <Link to="/cgu" className="text-brand-orange hover:underline">Conditions Générales d'Utilisation</Link> · <Link to="/cookies" className="text-brand-orange hover:underline">Politique Cookies</Link> · <Link to="/mentions-legales" className="text-brand-orange hover:underline">Mentions légales</Link></p>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
